import { IonAlert, IonApp, IonRouterOutlet, setupConfig } from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
/* Core CSS required for Ionic components to work properly */
import "@ionic/react/css/core.css";
/* Optional CSS utils that can be commented out */
import "@ionic/react/css/display.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/float-elements.css";
/* Basic CSS for apps built with Ionic */
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/padding.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";
import "@ionic/react/css/typography.css";
import React, { useEffect, useState } from "react";
import { Provider } from "react-redux";
import { Redirect, Route, useHistory } from "react-router-dom";
import { parseAppOpenUrl } from "./helpers/deep-link";
import { lt } from "semver";
import "./assets/css/global.css";
import LimeAPI from "./classes/LimeAPI";
import RequireAuth from "./classes/RequireAuth";
import withRouteData from "./classes/withRouteData";
import LoginError from "./components/Login/LoginError";
import LoginSuccess from "./components/Login/LoginSuccess";
import Menu from "./components/Menu/Menu";
import initAnalytics from "./helpers/analytics";
import PageEditProject from "./pages/PageEditProject";
import PageEditQualityDocument from "./pages/PageEditQualityDocument";
import PageInformation from "./pages/PageInformation";
import PageInformationDetail from "./pages/PageInformationDetail";
import PageLogin from "./pages/PageLogin";
import PageNewProject from "./pages/PageNewProject";
import PageNewQualityDocument from "./pages/PageNewQualityDocument";
import PageNewQualityDocumentV4 from "./pages/PageNewQualityDocumentV4";
import PageProject from "./pages/PageProject";
import PageProjects from "./pages/PageProjects";
import PageQualityDocument from "./pages/PageQualityDocument";
import store from "./store";
import "./theme.css";

const APP_VERSIONS = {
  ios: "1.9.15",
  android: "1.9.15",
  web: "1.9.15",
};

setupConfig({
  swipeBackEnabled: false,
});

function DeepLinkHandler() {
  const history = useHistory();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    let cancelled = false;
    let listener: { remove: () => Promise<void> } | undefined;

    const navigateDeepLink = (url: string) => {
      const target = parseAppOpenUrl(url);
      if (!target) {
        return;
      }

      history.replace(target.pathname + target.search);
    };

    void (async () => {
      const launch = await CapacitorApp.getLaunchUrl();
      if (launch?.url) {
        navigateDeepLink(launch.url);
      }

      listener = await CapacitorApp.addListener("appUrlOpen", ({ url }) => {
        navigateDeepLink(url);
      });
      if (cancelled) {
        await listener.remove();
      }
    })();

    return () => {
      cancelled = true;
      void listener?.remove();
    };
  }, [history]);

  return null;
}

function Routes({ analytics }: { analytics: boolean }) {
  return (
    <IonRouterOutlet id="primary">
      <Route path="/login" component={PageLogin} exact />
      <Route
        path="/login-review-test-for-ios-and-android"
        component={PageLogin}
        exact
      />
      <Route path="/login-success" component={LoginSuccess} exact />
      <Route path="/login-error" component={LoginError} exact />
      <Route path="/projects" component={RequireAuth(PageProjects)} exact />
      <Route
        path="/projects/new"
        component={RequireAuth(PageNewProject)}
        exact
      />
      <Route path="/projects/:id" component={RequireAuth(withRouteData(PageProject))} exact />
      <Route
        path="/projects/:id/edit"
        component={RequireAuth(withRouteData(PageEditProject))}
        exact
      />
      <Route
        path="/projects/:id/qualitydocument/new"
        component={RequireAuth(withRouteData(PageNewQualityDocument))}
        exact
      />
      <Route
        path="/projects/:id/qualitydocument/new-v4"
        component={RequireAuth(withRouteData(PageNewQualityDocumentV4))}
        exact
      />
      <Route
        path="/projects/:id/qualitydocument/:documentId/"
        component={RequireAuth(withRouteData(PageQualityDocument))}
        exact
      />
      <Route
        path="/projects/:id/qualitydocument/:documentId/edit"
        component={RequireAuth(withRouteData(PageEditQualityDocument))}
        exact
      />
      <Route
        path="/information"
        component={RequireAuth(PageInformation)}
        exact
      />
      <Route
        path="/information/:slug"
        component={PageInformationDetail}
        exact
      />
      <Route path="/" render={() => <Redirect to="/login" exact />} exact />
    </IonRouterOutlet>
  );
}

const App: React.FunctionComponent = () => {
  const [analytics, setAnalytics] = useState(false);
  const abortController = new AbortController();
  const [showUpdateModal, setShowUpdateModal] = useState(false);

  useEffect(() => {
    async function init() {
      await initAnalytics();
      setAnalytics(true);
    }

    // tslint:disable-next-line
    init();
  }, []);

  useEffect(() => {
    async function handleMinVersions() {
      const platform = Capacitor.getPlatform();

      // if (platform === "web") {
      //   return;
      // }

      const minVersions = await LimeAPI.get(
        "minVersions",
        abortController.signal,
      );

      if (!minVersions || minVersions.error) {
        return;
      }

      const appVersions: { [key: string]: string } = APP_VERSIONS;
      const currentVersion = appVersions[platform];
      const minVersion = minVersions[platform];

      if (typeof currentVersion !== "string" || typeof minVersion !== "string") {
        return;
      }

      if (lt(currentVersion, minVersion)) {
        setShowUpdateModal(true);
      }
    }

    handleMinVersions().catch(() => undefined);

    return () => {
      abortController.abort();
    };
  }, []);

  return (
    <IonApp>
      <Provider store={store}>
        <IonReactRouter>
          <DeepLinkHandler />
          <Menu />
          <Routes analytics={analytics} />
        </IonReactRouter>
      </Provider>
      <IonAlert
        isOpen={showUpdateModal}
        backdropDismiss={false}
        header="Uppdatering krävs"
        message="För att kunna ta del av alla funktioner i appen, vänligen uppdatera till den senaste versionen."
        buttons={[
          {
            text: "Uppdatera nu",
            handler: () => {
              window.location.href =
                Capacitor.getPlatform() === "ios"
                  ? "https://apps.apple.com/se/app/byggkeramikr%C3%A5det/id1503332901"
                  : "https://play.google.com/store/apps/details?id=se.bkr";
            },
          },
        ]}
      />
    </IonApp>
  );
};

export default App;
